import dayjs from 'dayjs'
import { fmtDisplayDate } from '@utils/displayDate'

/**
 * Comparative P&L: which comparison window cb2 ASKS CB for, and which ranges it
 * refuses to ask about at all (spec 127 §4.3 (1)–(3), CC-53903).
 *
 * ─── The defect ─────────────────────────────────────────────────────────────
 * `PLReport.toWire` hard-coded `endAdjust:'year'`, `dateRangeStr:'This Year'`,
 * `isAdvanced:false`, `advancedRange:0`. The proc's Prior Period branch
 * (`cb_Accounting_GetPLReport_(0).sql:511-575`) reads exactly those four keys to
 * pick the comparison window, so every Prior Period run compared against the
 * 12 months ending the day before the range started — whatever the range. Measured
 * live: Q2 2025 compared with 04/01/2024–03/31/2025 (Net Income 1,052,862.36)
 * instead of Q1 2025 (553,208.57). CB reports the window it used in
 * `report.startDate`/`endDate`, so the wrong window was observable on every run.
 *
 * ─── What this module does, and what it never does ─────────────────────────
 * It chooses WHICH of CB's branches to ask for, from the SHAPE of the two dates
 * alone. It never computes a window and never touches a figure: CB's proc is the
 * only thing that turns these keys into dates, and CB's payload is the only source
 * of the numbers. It is deliberately today-independent — cb2's date control stores
 * dates, not presets (`specializedFilterFields.js` dateRangeField), so a saved
 * view, a queued job and a schedule must reproduce the same window from the same
 * two dates.
 *
 * The rule, checked in this order (spec 127 §4.3 table; every branch confirmed by
 * a live run, not by the repo's proc copy, which is not byte-identical to the
 * deployed one):
 *
 *   0  a bound missing, unparseable, or end < start  → refused before the call
 *   1  whole calendar months, N = 1                  → month   / This Month
 *   2  whole months, N = 3, starting Jan/Apr/Jul/Oct → quarter / This Quarter
 *   3  whole months, N = 12, starting January        → year    / This Year
 *   4  any other N whole months (H1, Jan–May, Jul–Jun)
 *                                                    → month, isAdvanced, advancedRange N−1 / Custom
 *                                                      (the N months immediately before —
 *                                                       Jordan's decision Q1, 2026-09-29)
 *   5  starts on the 1st, ends mid-month, same month → month   / This Month to Date
 *   6  starts Jan 1, ends mid-month, same year       → year    / This Year to Date
 *   7  anything else (a day, a week, QTD, mid-month spans, multi-year)
 *                                                    → day     / Custom
 *
 * Rule 4 MUST be tested before rule 6: H1 (01/01–06/30) is also "starts Jan 1,
 * same year", and as YTD it would compare with Jan–Jun of the previous year
 * instead of the six months immediately before. A SINGLE DAY goes to rule 7
 * before rules 5 and 6 are tried: a one-day range on the 1st (or on Jan 1) is
 * "a day", not a month- or year-to-date.
 *
 * Only Prior Period reads these keys. Prior Year and Year-to-Date were measured
 * indifferent to them (identical windows and figures on 24 presets + 8 typed
 * ranges), so the page sends them only for Prior Period and every other wire —
 * and its job dedupe key — stays byte-identical.
 */

export const REFUSE_OPEN_RANGE = 'open-range'
export const REFUSE_INVERTED = 'inverted-range'

/** The note shown (role="status") when a comparison run is refused. */
export const COMPARE_RANGE_MESSAGES = {
  [REFUSE_OPEN_RANGE]: 'Choose a start and an end date to compare periods.',
  [REFUSE_INVERTED]: 'The start date is after the end date.',
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * A 'YYYY-MM-DD' naming a real calendar day, else null. Strict on purpose: dayjs's
 * lenient constructor rolls 2025-02-30 over to 03/02 and would silently ask CB
 * about a range nobody typed.
 */
const parseIsoDate = (v) => {
  if (typeof v !== 'string' || !ISO_DATE.test(v)) return null
  const d = dayjs(v)
  return d.isValid() && d.format('YYYY-MM-DD') === v ? d : null
}

/**
 * Why a comparison over this range must not be requested, or null when it may.
 * Rule 0. Measured: an open-ended range comes back from CB as an HTTP 500 or as a
 * comparison window ending 9998-12-31 — a plausible statement of nothing. V1
 * refuses too (`AccountingPAndLComparisonReportView.js:507-520`).
 */
export const compareRangeRefusal = (startDate, endDate) => {
  const s = parseIsoDate(startDate)
  const e = parseIsoDate(endDate)
  if (!s || !e) return REFUSE_OPEN_RANGE
  if (e.isBefore(s, 'day')) return REFUSE_INVERTED
  return null
}

/**
 * The four control-less Prior Period wire keys for a range, or `{ refuse }`.
 *
 * @param {string} startDate 'YYYY-MM-DD'
 * @param {string} endDate   'YYYY-MM-DD'
 * @returns {{endAdjust: string, isAdvanced: boolean, advancedRange: number, dateRangeStr: string}
 *           | {refuse: string}}
 */
export const priorPeriodWire = (startDate, endDate) => {
  const refuse = compareRangeRefusal(startDate, endDate)
  if (refuse) return { refuse }
  const s = parseIsoDate(startDate)
  const e = parseIsoDate(endDate)
  const simple = (endAdjust, dateRangeStr) => ({ endAdjust, isAdvanced: false, advancedRange: 0, dateRangeStr })

  const startsMonth = s.date() === 1
  const endsMonth = e.date() === e.daysInMonth()
  const months = (e.year() - s.year()) * 12 + (e.month() - s.month()) + 1

  if (startsMonth && endsMonth) {
    if (months === 1) return simple('month', 'This Month')
    if (months === 3 && s.month() % 3 === 0) return simple('quarter', 'This Quarter')
    if (months === 12 && s.month() === 0) return simple('year', 'This Year')
    // Rule 4: CB's month branch with periodDiff = advancedRange + 1 (proc :520-527,
    // :550-554) — the wire V1's Advanced "past N months" tab sends.
    return { endAdjust: 'month', isAdvanced: true, advancedRange: months - 1, dateRangeStr: 'Custom' }
  }
  // A single day is rule 7's "a day", whatever the date. Checked before rules 5
  // and 6, which a one-day range on the 1st (or on Jan 1) would otherwise match:
  // measured live (review of spec 127), 05/01/2025 alone as "This Month to Date"
  // compared with 04/01/2025 and 01/01/2025 alone as "This Year to Date" with
  // 01/01/2024, where V1's Today / Yesterday / Tomorrow send 'day' and CB compares
  // the previous day (04/30/2025, 12/31/2024).
  if (s.isSame(e, 'day')) return simple('day', 'Custom')
  const sameYear = s.year() === e.year()
  if (startsMonth && sameYear && s.month() === e.month()) return simple('month', 'This Month to Date')
  if (sameYear && s.month() === 0 && s.date() === 1) return simple('year', 'This Year to Date')
  // Rule 7: CB's same-length window ending the day before (proc :565-568).
  return simple('day', 'Custom')
}

/**
 * The comparison window CB says it used — `report.startDate`/`endDate` on a
 * comparison run (spec 127 §4.3 (3)) — as display dates, or null when either is
 * missing or unparseable (a blank is visibly missing; a guessed date is not).
 * Only meaningful on a COMPARISON run: on a plain run the same two fields carry the
 * run's own range, so the caller gates on the run's compare mode.
 */
export const comparisonWindowOf = (report) => {
  const start = fmtDisplayDate(report?.startDate)
  const end = fmtDisplayDate(report?.endDate)
  return start && end ? { start, end } : null
}

/** "01/01/2025 to 03/31/2025" — ' to ', never an arrow (jsPDF's WinAnsi, see reportFilterSummary.js). */
export const describeComparisonWindow = (w) => (w ? `${w.start} to ${w.end}` : '')
